import React from 'react';
import { 
  Leaf, 
  Droplets, 
  Sparkles, 
  ShieldCheck, 
  Package,
  ArrowLeft,
  ArrowRight,
  Flame,
  Heart,
  Stethoscope,
  Sun,
  Flower,
  Coffee,
  Activity,
  Pill,
  Zap
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';

const iconMap = {
  Leaf,
  Droplets,
  Sparkles,
  ShieldCheck,
  Package,
  Flame,
  Heart,
  Stethoscope,
  Sun,
  Flower,
  Coffee,
  Activity,
  Pill,
  Zap
};

const CategoryGrid = ({ onSelectCategory }) => {
  const { lang, isRtl } = useLanguage();
  const { cmsContent, categories, products = [] } = useStore();

  const title = lang === 'ur'
    ? (cmsContent?.sectionTitles?.categoriesTitleUrdu || 'قانون مفرد اعضاء و ہربل کیٹیگریز')
    : (cmsContent?.sectionTitles?.categoriesTitleEn || 'Tibb-e-Pakistani & Herbal Categories');

  const subtitle = lang === 'ur'
    ? (cmsContent?.sectionTitles?.categoriesSubtitleUrdu || 'اعضائے رئیسہ (اعصاب، عضلات، غدد) کے لیے مستند و خالص نباتاتی مرکبات')
    : (cmsContent?.sectionTitles?.categoriesSubtitleEn || 'Formulations categorized by elementary organ tissues for targeted natural healing');

  const tag = lang === 'ur'
    ? (cmsContent?.sectionTitles?.categoriesTagUrdu || 'اقسام ادویات و پنسار')
    : (cmsContent?.sectionTitles?.categoriesTagEn || 'Product Categories');

  return (
    <section className="py-12 md:py-16 bg-herbal-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 space-y-2">
          <div className="inline-block px-3 py-1 rounded-full bg-pansar-100 text-pansar-800 text-xs font-bold font-title">
            {tag}
          </div>
          <h2 className="text-2xl sm:text-4xl font-black text-pansar-950 font-title">
            {title}
          </h2>
          <p className="text-sm text-gray-600 font-urdu">
            {subtitle}
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
          {(categories || [])
            .filter((cat) => cat.showOnHome !== false)
            .sort((a, b) => (Number(a.order) || 99) - (Number(b.order) || 99))
            .map((cat) => {
            const Icon = iconMap[cat.icon] || Leaf;
            const realProductCount = (products || []).filter(p => p.category === cat.id).length;
            const countLabel = lang === 'ur' 
              ? `${realProductCount} اشیاء` 
              : `${realProductCount} ${realProductCount === 1 ? 'Item' : 'Items'}`;

            return (
              <div
                key={cat.id}
                onClick={() => onSelectCategory(cat.id)}
                className="group relative cursor-pointer overflow-hidden rounded-3xl bg-white p-6 shadow-sm border border-emerald-900/10 hover:border-pansar-500 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col justify-between"
              >
                {/* Top Badge & Icon */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-pansar-50 group-hover:bg-pansar-700 text-pansar-800 group-hover:text-amber-300 flex items-center justify-center transition-colors duration-300 shadow-inner overflow-hidden">
                      {cat.image ? (
                        <img src={cat.image} alt={cat.nameUrdu} className="w-full h-full object-cover" />
                      ) : (
                        <Icon className="w-6 h-6" />
                      )}
                    </div>
                    <span className="text-[11px] font-semibold text-gray-400 group-hover:text-pansar-700 transition-colors font-title">
                      {countLabel}
                    </span>
                  </div>

                  <h3 className="font-extrabold text-lg text-gray-900 group-hover:text-pansar-800 transition-colors font-subheading mb-1">
                    {lang === 'ur' ? cat.nameUrdu : cat.nameEn}
                  </h3>

                  <p className="text-xs text-gray-500 font-urdu leading-relaxed line-clamp-2">
                    {lang === 'ur' ? cat.taglineUrdu : cat.taglineEn}
                  </p>
                </div>

                {/* Bottom Action */}
                <div className="mt-5 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-pansar-700 group-hover:text-pansar-900 font-urdu">
                  <span>{lang === 'ur' ? 'اشیاء دیکھیں' : 'Explore Items'}</span>
                  {isRtl ? (
                    <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" />
                  ) : (
                    <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default CategoryGrid;
